import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users, Clock, Send, Check, X, UserPlus, Heart, Building2 } from "lucide-react";
import { VerifiedBadge, getTier } from "@/components/VerifiedBadge";
import { Layout } from "@/components/Layout";
import { DiscoverCard } from "@/components/DiscoverCard";
import { NetworkActions } from "@/components/NetworkActions";
import { Button } from "@/components/ui/button";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";

const TABS = [
  ["connections", "Connections", Users],
  ["pending", "Requests", Clock],
  ["sent", "Sent", Send],
  ["following", "Following", Heart],
  ["followers", "Followers", UserPlus],
];

export default function Network() {
  const [tab, setTab] = useState("connections");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const load = useCallback(async (t) => {
    const my = ++seq.current;
    setLoading(true);
    const urls = { connections: "/connections", pending: "/connections/pending", sent: "/connections/sent", following: "/following", followers: "/followers" };
    try { const { data } = await api.get(urls[t]); if (my === seq.current) setData(data); }
    catch (e) { if (my === seq.current) setData([]); }
    finally { if (my === seq.current) setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const act = async (cid, action) => {
    try { await api.post(`/connections/${cid}/${action}`); toast.success(`Request ${action}ed`); load(tab); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isConnList = ["connections", "pending", "sent"].includes(tab);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <Users className="text-brand" size={24} />
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">My Network</h1>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {TABS.map(([v, label, Icon]) => (
            <button key={v} data-testid={`network-tab-${v}`} onClick={() => { if (v === tab) return; setData([]); setTab(v); }} className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${tab === v ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={30} /></div>
        ) : data.length === 0 ? (
          <div data-testid="network-empty" className="py-16 text-center text-slate-500">Nothing here yet. Head to Discover to grow your network.</div>
        ) : isConnList ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="network-list">
            {data.map((c) => (
              <div key={c.connection_id} className="bg-white border border-slate-200 rounded-xl p-5">
                <DiscoverCardInline business={c.business} />
                {tab === "connections" && (
                  <div className="mt-3"><NetworkActions business={{ ...c.business, connection: { status: "accepted", connection_id: c.connection_id } }} onChange={() => load(tab)} /></div>
                )}
                {tab === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <Button data-testid={`accept-${c.connection_id}`} onClick={() => act(c.connection_id, "accept")} className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 gap-1.5 font-semibold"><Check size={15} /> Accept</Button>
                    <Button data-testid={`reject-${c.connection_id}`} onClick={() => act(c.connection_id, "reject")} variant="outline" className="flex-1 h-9 gap-1.5 font-semibold border-slate-300"><X size={15} /> Reject</Button>
                  </div>
                )}
                {tab === "sent" && (
                  <Button data-testid={`cancel-${c.connection_id}`} onClick={() => act(c.connection_id, "cancel")} variant="outline" className="w-full h-9 mt-3 gap-1.5 font-semibold border-slate-300"><X size={15} /> Cancel request</Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="network-list">
            {[...new Map(data.map((b) => [b.id, b])).values()].map((b) => <DiscoverCard key={b.id} business={b} onChange={() => load(tab)} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}

const DiscoverCardInline = ({ business }) => {
  const logo = mediaUrl(business.logo_url);
  return (
    <Link to={`/business/${business.slug || business.id}`} className="flex items-center gap-3">
      <div className="h-12 w-12 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
        {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <Building2 className="text-slate-300" size={20} />}
      </div>
      <div className="min-w-0">
        <p className="font-display font-bold text-slate-900 truncate flex items-center gap-1">{business.business_name}<VerifiedBadge tier={getTier(business)} size="sm" iconOnly /></p>
        <p className="text-xs text-slate-500 truncate">{business.category}{business.city ? ` · ${business.city}` : ""}</p>
      </div>
    </Link>
  );
};
